import { useRef } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { trapTab, useDialogFocus, useScrollLock } from '../../components/CheckoutModal';
import { useListingWords } from './listingWords';
import './elementMediaLightbox.css';
import { responsive } from './responsiveImage';

// Sleek's picture viewer: a product picture opened full screen over a white page, a close button in the
// corner, arrows either side and "2 / 6" under it. It is a real dialog — focus moves in, Tab stays in,
// Escape closes, the arrow keys step — and focus goes back to the picture it was opened from.

/** One picture the viewer shows. */
export type ViewedPicture = { src: string; alt: string };

/** What the viewer shows: the pictures, which one, how to move, and how to close. */
type LightboxProps = { pictures: readonly ViewedPicture[]; at: number; onMove: (at: number) => void; onClose: () => void };

// Draws the viewer on one picture, stepping round from the last to the first.
export const MediaLightbox: React.FC<LightboxProps> = ({ pictures, at, onMove, onClose }) => {
  const panel = useRef<HTMLDivElement>(null);
  const words = useListingWords();
  useDialogFocus(true, panel);
  useScrollLock(true);
  const step = (by: number) => onMove((at + by + pictures.length) % pictures.length);
  const keys = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') return onClose();
    if (event.key === 'ArrowRight') return step(1);
    if (event.key === 'ArrowLeft') return step(-1);
    if (event.key === 'Tab') trapTab(event, panel.current);
  };
  const picture = pictures[at];
  return (
    <div className="xe-lightbox" role="dialog" aria-modal="true" aria-label={picture?.alt || words.pictures} tabIndex={-1} ref={panel} onKeyDown={keys}>
      <button type="button" className="xe-lightbox-close" aria-label={words.close} onClick={onClose}><X size={22} aria-hidden="true" /></button>
      {picture ? <img className="xe-lightbox-picture" {...responsive(picture.src, '100vw')} alt={picture.alt} /> : null}
      {pictures.length > 1 ? (
        <div className="xe-lightbox-nav">
          <button type="button" aria-label={words.previousPicture} onClick={() => step(-1)}><ChevronLeft size={22} aria-hidden="true" /></button>
          <span aria-live="polite">{at + 1} / {pictures.length}</span>
          <button type="button" aria-label={words.nextPicture} onClick={() => step(1)}><ChevronRight size={22} aria-hidden="true" /></button>
        </div>
      ) : null}
    </div>
  );
};
